import React, { useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Collapse,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    IconButton,
    Tooltip,
    Icon,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import EmptyStateCard from '../../compnents/EmptyStateCard';
import ScoreFreshnessBadge from '../../compnents/ScoreFreshnessBadge';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardComplianceScoresPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [pagination, setPagination] = useState({ total_count: 0, page: 1, page_size: 25 });
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const [customerScores, setCustomerScores] = useState({});
    const [loadingScores, setLoadingScores] = useState({});
    const customerRefs = useRef({});
    const containerRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const isScrollingRef = useRef(false);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const strongBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : 'gray.300';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.25)' : '#E2E8F0';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';
    const recommendationBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : '#DCEEFF';

    // Placeholder/empty background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Chart axis color
    const axisColor = isDarkTheme ? bodyTextColor : '#4A5568';

    // Chart colors - brighter for dark theme
    const radarColor = isDarkTheme ? accentColor : primaryColor;

    // Fetch customers list
    const fetchCustomers = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const params = { page, page_size: 25 };
            if (search) params.search = search;

            const res = await axios.get(`${API_BASE_URL}/content/list-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setCustomers(res.data.customers);
            setPagination(res.data.pagination);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customers.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch customer scores
    const fetchCustomerScores = async (customerId) => {
        if (customerScores[customerId]) return; // Already loaded

        try {
            setLoadingScores(prev => ({ ...prev, [customerId]: true }));
            const res = await axios.get(`${API_BASE_URL}/content/admin-customers/${customerId}/scores`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCustomerScores(prev => ({ ...prev, [customerId]: res.data }));
        } catch (error) {
            toast({
                title: 'Error',
                description: `Failed to load scores for customer ${customerId}.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoadingScores(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Store current scroll position
    const storeScrollPosition = () => {
        scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    };

    // Restore scroll position
    const restoreScrollPosition = () => {
        if (isScrollingRef.current) return;
        
        isScrollingRef.current = true;
        window.scrollTo({
            top: scrollPositionRef.current,
            behavior: 'auto'
        });
        
        // Reset the flag after a short delay
        setTimeout(() => {
            isScrollingRef.current = false;
        }, 100);
    };

    // Toggle customer details with scroll prevention
    const toggleCustomer = async (customerId) => {
        const wasExpanded = expandedCustomer === customerId;
        
        if (wasExpanded) {
            // If clicking the same customer, just close it
            setExpandedCustomer(null);
        } else {
            // Store current scroll position BEFORE any state changes
            storeScrollPosition();
            
            const previouslyExpanded = expandedCustomer;
            
            // Set the new expanded customer
            setExpandedCustomer(customerId);
            await fetchCustomerScores(customerId);

            // If we're switching from one customer to another, prevent the auto-scroll
            if (previouslyExpanded !== null) {
                // Use setTimeout to ensure the DOM has updated, then restore position
                setTimeout(() => {
                    restoreScrollPosition();
                }, 10);
            } else {
                // If no customer was previously open, we can scroll to the new one
                // but only if it's not already in view
                setTimeout(() => {
                    const element = customerRefs.current[customerId];
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        // Only scroll if the element is not in the viewport
                        if (rect.top < 0 || rect.bottom > window.innerHeight) {
                            element.scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest'
                            });
                        }
                    }
                }, 100);
            }
        }
    };

    // Enhanced scroll handler to prevent unwanted scrolling during customer expansion
    useEffect(() => {
        const handleScroll = () => {
            if (isScrollingRef.current) {
                // If we're programmatically scrolling, update our stored position
                scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Initial load
    useEffect(() => {
        fetchCustomers();
        // eslint-disable-next-line
    }, []);

    // Handle search
    const handleSearch = () => {
        fetchCustomers(1, searchTerm);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        fetchCustomers(1, '');
    };

    // Get color for bias score with gradient
    const getScoreColor = (score) => {
        // Color gradient from green (low) to red (high)
        if (score >= 80) return '#8B0000'; // Dark red
        if (score >= 70) return '#DC143C'; // Crimson
        if (score >= 60) return '#FF6347'; // Tomato
        if (score >= 50) return '#FF8C00'; // Dark orange
        if (score >= 40) return '#FFA500'; // Orange
        if (score >= 30) return '#FFD700'; // Gold
        if (score >= 20) return '#FFFFE0'; // Light yellow
        if (score >= 10) return '#90EE90'; // Light green
        return '#228B22'; // Forest green
    };

    // Get risk alignment color
    const getRiskAlignmentColor = (status) => {
        if (status === 'high') return 'green';
        if (status === 'medium') return 'yellow';
        return 'red';
    };

    // Render bias chart
    const renderBiasChart = (biases) => {
        const biasData = Object.entries(biases).map(([key, value]) => ({
            name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            score: value.score,
            confidence: value.confidence,
        }));

        return (
            <ResponsiveContainer width="100%" height={400}>
                <BarChart data={biasData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                        dataKey="name"
                        stroke={axisColor}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        interval={0}
                        tick={{ fontSize: 10, fill: axisColor }}
                    />
                    <YAxis
                        type="number"
                        domain={[0, 100]}
                        stroke={axisColor}
                        label={{ value: 'Score', angle: -90, position: 'insideLeft', fill: axisColor }}
                        tick={{ fill: axisColor }}
                    />
                    <RechartsTooltip
                        contentStyle={{ 
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                            border: `1px solid ${borderColor}`, 
                            borderRadius: '8px',
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                        }}
                        formatter={(value) => [`${value}`, 'Score']}
                    />
                    <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                        {biasData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // Render seaborn-style heatmap
    const renderBiasHeatmap = (biases) => {
        const biasEntries = Object.entries(biases);
        const cols = 5; // Fixed number of columns
        const rows = Math.ceil(biasEntries.length / cols);

        return (
            <Box
                bg={isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : cardBg}
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor={strongBorderColor}
                overflow="auto"
            >
                {/* Color scale legend */}
                <Flex justify="flex-end" mb={4} align="center">
                    <Text fontSize="xs" fontWeight="bold" mr={2} color={bodyTextColor}>
                        Score:
                    </Text>
                    <HStack spacing={0}>
                        {[0, 20, 40, 60, 80, 100].map((val) => (
                            <Box key={val} textAlign="center">
                                <Box
                                    w="30px"
                                    h="15px"
                                    bg={getScoreColor(val)}
                                    border="1px solid"
                                    borderColor={strongBorderColor}
                                />
                                <Text fontSize="9px" color={bodyTextColor}>{val}</Text>
                            </Box>
                        ))}
                    </HStack>
                </Flex>

                {/* Heatmap grid */}
                <Box
                    display="inline-block"
                    border="1px solid"
                    borderColor={strongBorderColor}
                >
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <Flex key={rowIndex}>
                            {Array.from({ length: cols }).map((_, colIndex) => {
                                const index = rowIndex * cols + colIndex;
                                if (index >= biasEntries.length) {
                                    return (
                                        <Box
                                            key={colIndex}
                                            w="140px"
                                            h="80px"
                                            bg={placeholderBg}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={strongBorderColor}
                                        />
                                    );
                                }

                                const [key, value] = biasEntries[index];
                                const biasName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                return (
                                    <Tooltip
                                        key={colIndex}
                                        label={
                                            <Box>
                                                <Text fontWeight="bold">{biasName}</Text>
                                                <Text>Score: {value.score}</Text>
                                                <Text>Confidence: {(value.confidence * 100).toFixed(0)}%</Text>
                                            </Box>
                                        }
                                        placement="top"
                                        hasArrow
                                        bg={isDarkTheme ? 'gray.800' : 'gray.700'}
                                        color="white"
                                    >
                                        <Box
                                            w="140px"
                                            h="80px"
                                            bg={getScoreColor(value.score)}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={strongBorderColor}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            transition="all 0.2s"
                                            position="relative"
                                            _hover={{
                                                transform: 'scale(1.05)',
                                                zIndex: 10,
                                                boxShadow: '0 0 10px rgba(0,0,0,0.3)',
                                            }}
                                        >
                                            <Text
                                                fontSize="xs"
                                                fontWeight="bold"
                                                color={value.score > 50 ? "white" : "gray.800"}
                                                textAlign="center"
                                                px={2}
                                                noOfLines={2}
                                                lineHeight="1.2"
                                            >
                                                {biasName}
                                            </Text>
                                            <Text
                                                fontSize="2xl"
                                                fontWeight="bold"
                                                color={value.score > 50 ? "white" : "gray.800"}
                                                mt={1}
                                            >
                                                {value.score}
                                            </Text>
                                            <Badge
                                                colorScheme={value.confidence >= 0.8 ? "green" : "yellow"}
                                                fontSize="8px"
                                                position="absolute"
                                                top={1}
                                                right={1}
                                            >
                                                {(value.confidence * 100).toFixed(0)}%
                                            </Badge>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Flex>
                    ))}
                </Box>
            </Box>
        );
    };

    // Render radar chart for top biases
    const renderRadarChart = (biases) => {
        const topBiases = Object.entries(biases)
            .sort((a, b) => b[1].score - a[1].score)
            .slice(0, 8)
            .map(([key, value]) => ({
                bias: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                score: value.score,
            }));

        return (
            <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={topBiases}>
                    <PolarGrid stroke={chartGridColor} />
                    <PolarAngleAxis 
                        dataKey="bias" 
                        stroke={axisColor} 
                        tick={{ fontSize: 8, fill: axisColor }} 
                    />
                    <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        stroke={axisColor} 
                        tick={false} 
                        axisLine={false} 
                    />
                    <Radar 
                        name="Bias Score" 
                        dataKey="score" 
                        stroke={radarColor} 
                        fill={radarColor} 
                        fillOpacity={0.6} 
                    />
                    <RechartsTooltip 
                        contentStyle={{ 
                            backgroundColor: isDarkTheme ? '#2D3748' : cardBg, 
                            border: `1px solid ${borderColor}`, 
                            borderRadius: '8px', 
                            color: isDarkTheme ? '#E2E8F0' : '#1A202C'
                        }} 
                    />
                </RadarChart>
            </ResponsiveContainer>
        );
    };

    // Render product recommendation
    const renderProductRecommendation = (productRec) => {
        if (!productRec) return null;

        const {
            recommended_product_type,
            product_code,
            characteristics,
            bias_addresses,
            rationale,
            confidence,
            alternative_products,
            risk_alignment,
            source,
            model_alignment,
        } = productRec;

        const sourceColor = source === 'tenant_catalog' ? 'green' : 'gray';
        const sourceLabel = source === 'tenant_catalog' ? 'Tenant catalog' : 'Default catalog';
        const alignmentMeta = (() => {
            if (!model_alignment) return null;
            if (model_alignment.status === 'in_model') {
                return { color: 'green', label: 'In model band' };
            }
            if (model_alignment.status === 'outside_model') {
                return { color: 'orange', label: 'Outside customer model' };
            }
            return { color: 'gray', label: 'Model unknown' };
        })();

        return (
            <Card
                bg={cardBg}
                borderRadius="lg"
                boxShadow="md"
                borderWidth="2px"
                borderColor={isDarkTheme ? accentColor : primaryColor}
            >
                <CardBody>
                    <VStack spacing={4} align="stretch">
                        {/* Header */}
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                            <Box>
                                <Heading size="md" color={headingTextColor}>
                                    Product-Fit Signal
                                </Heading>
                                <Text fontSize="xs" color={mutedTextColor} mt={1}>
                                    Decision support for the adviser review — not investment advice to the end client.
                                </Text>
                            </Box>
                            <HStack spacing={2} flexWrap="wrap">
                                {source && (
                                    <Badge colorScheme={sourceColor} fontSize="xs" px={2} py={1}>
                                        {sourceLabel}
                                    </Badge>
                                )}
                                {alignmentMeta && (
                                    <Badge colorScheme={alignmentMeta.color} fontSize="xs" px={2} py={1}>
                                        {alignmentMeta.label}
                                    </Badge>
                                )}
                                <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                    {(confidence * 100).toFixed(0)}% Fit
                                </Badge>
                            </HStack>
                        </Flex>

                        {/* Recommended Product */}
                        <Box bg={infoBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                            <HStack justify="space-between" mb={2}>
                                <Heading size="sm" color={headingTextColor}>
                                    {recommended_product_type}
                                </Heading>
                                <Badge colorScheme="blue" fontSize="sm">
                                    {product_code}
                                </Badge>
                            </HStack>

                            {/* Rationale */}
                            <VStack align="stretch" spacing={2} mt={3}>
                                {rationale && rationale.map((item, index) => (
                                    <HStack key={index} align="start">
                                        <Icon as={CheckCircleIcon} color={successColor} mt={1} />
                                        <Text fontSize="sm" color={labelTextColor}>
                                            {item}
                                        </Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>

                        {/* Risk Alignment */}
                        {risk_alignment && (
                            <Box bg={placeholderBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                <Heading size="sm" mb={3} color={labelTextColor}>
                                    Risk Alignment
                                </Heading>
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={bodyTextColor}>Status</StatLabel>
                                        <StatNumber>
                                            <Badge colorScheme={getRiskAlignmentColor(risk_alignment.status)} fontSize="md">
                                                {risk_alignment.status.toUpperCase()}
                                            </Badge>
                                        </StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={bodyTextColor}>Product Risk</StatLabel>
                                        <StatNumber fontSize="lg" color={labelTextColor}>{risk_alignment.product_risk}/10</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={bodyTextColor}>Customer Tolerance</StatLabel>
                                        <StatNumber fontSize="lg" color={labelTextColor}>{risk_alignment.customer_tolerance}/10</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={bodyTextColor}>Harmony Score</StatLabel>
                                        <StatNumber fontSize="lg" color={labelTextColor}>{(risk_alignment.harmony * 100).toFixed(0)}%</StatNumber>
                                    </Stat>
                                </SimpleGrid>
                            </Box>
                        )}

                        {/* Product Characteristics */}
                        {characteristics && (
                            <Box>
                                <Heading size="sm" mb={3} color={labelTextColor}>
                                    Product Characteristics
                                </Heading>
                                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                                    {Object.entries(characteristics).map(([key, value]) => (
                                        <HStack key={key} spacing={2}>
                                            <Badge
                                                colorScheme={value === true ? "green" : value === false ? "gray" : "blue"}
                                                fontSize="xs"
                                            >
                                                {key.replace(/_/g, ' ')}
                                            </Badge>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                {typeof value === 'boolean' ? (value ? '✓' : '✗') : value}
                                            </Text>
                                        </HStack>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        )}

                        {/* Biases Addressed */}
                        {bias_addresses && Object.keys(bias_addresses).length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color={labelTextColor}>
                                    Biases Addressed
                                </Heading>
                                <HStack spacing={3} flexWrap="wrap">
                                    {Object.entries(bias_addresses).map(([bias, score]) => (
                                        <Badge key={bias} colorScheme="purple" fontSize="sm" px={3} py={1}>
                                            {bias.replace(/_/g, ' ')}: {(score * 100).toFixed(0)}%
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}

                        {/* Alternative Products */}
                        {alternative_products && alternative_products.length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color={labelTextColor}>
                                    Alternative Products
                                </Heading>
                                <VStack spacing={2} align="stretch">
                                    {alternative_products.map((alt, index) => (
                                        <Box key={index} bg={placeholderBg} p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                            <HStack justify="space-between" mb={1}>
                                                <Text fontWeight="bold" fontSize="sm" color={labelTextColor}>
                                                    {alt.product_type}
                                                </Text>
                                                <Badge colorScheme="blue" fontSize="xs">
                                                    Score: {(alt.score * 100).toFixed(0)}%
                                                </Badge>
                                            </HStack>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                {alt.product_code}
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor} mt={1}>
                                                {alt.rationale}
                                            </Text>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                </CardBody>
            </Card>
        );
    };

    // Render customer scores
    const renderCustomerScores = (customerId) => {
        if (loadingScores[customerId]) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Spinner size="xl" color={primaryColor} />
                </Flex>
            );
        }

        const scoreData = customerScores[customerId];
        if (!scoreData || !scoreData.history || scoreData.history.length === 0) {
            return <Text color={mutedTextColor}>No score data available.</Text>;
        }

        const latestScore = scoreData.history[0];
        const { biases, risk, knowledge, rationale, product_recommendation } = latestScore;
        const scoreIsActionable = latestScore.actionable !== false;

        return (
            <VStack spacing={6} align="stretch">
                <HStack justify="space-between" align="start" flexWrap="wrap">
                    <ScoreFreshnessBadge freshness={latestScore.freshness} />
                    {!scoreIsActionable ? (
                        <Text fontSize="sm" color={mutedTextColor} fontWeight="semibold" maxW="2xl">
                            {latestScore.recommended_action || 'Refresh customer data before using these scores.'}
                        </Text>
                    ) : null}
                </HStack>
                <Box opacity={scoreIsActionable ? 1 : 0.45}>
                <VStack spacing={6} align="stretch">
                {/* Summary Stats */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card 
                        bg={infoBg} 
                        borderRadius="lg" 
                        boxShadow="sm" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Stat>
                                <StatLabel color={bodyTextColor}>Risk Tolerance</StatLabel>
                                <StatNumber color={isDarkTheme ? accentColor : primaryColor}>{risk.tolerance}/10</StatNumber>
                                <StatHelpText color={mutedTextColor}>Capacity: {risk.capacity}/10</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                    <Card 
                        bg={infoBg} 
                        borderRadius="lg" 
                        boxShadow="sm" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Stat>
                                <StatLabel color={bodyTextColor}>Risk Harmony</StatLabel>
                                <StatNumber color={isDarkTheme ? accentColor : primaryColor}>{(risk.harmony * 100).toFixed(0)}%</StatNumber>
                                <StatHelpText color={mutedTextColor}>Alignment Score</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                    <Card 
                        bg={infoBg} 
                        borderRadius="lg" 
                        boxShadow="sm" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Stat>
                                <StatLabel color={bodyTextColor}>Knowledge Level</StatLabel>
                                <StatNumber color={isDarkTheme ? accentColor : primaryColor}>
                                    {(knowledge.blended.equities * 100).toFixed(0)}%
                                </StatNumber>
                                <StatHelpText color={mutedTextColor}>Blended Equities</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Product Recommendation */}
                {product_recommendation && renderProductRecommendation(product_recommendation)}

                {/* Bias Heatmap */}
                <Card 
                    bg={cardBg} 
                    borderRadius="lg" 
                    boxShadow="md" 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <CardBody>
                        <Heading size="md" mb={4} color={headingTextColor}>
                            Behavioral Bias Heatmap
                        </Heading>
                        <Text fontSize="sm" color={bodyTextColor} mb={4}>
                            Color intensity indicates bias severity. Hover over cells for detailed information.
                        </Text>
                        {renderBiasHeatmap(biases)}
                    </CardBody>
                </Card>

                {/* Radar Chart and Bar Chart Side by Side */}
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Heading size="md" mb={4} color={headingTextColor}>
                                Top 8 Biases (Radar)
                            </Heading>
                            {renderRadarChart(biases)}
                        </CardBody>
                    </Card>
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Heading size="sm" mb={4} color={headingTextColor}>
                                All Bias Scores
                            </Heading>
                            <Box maxH="300px" overflowY="auto">
                                {renderBiasChart(biases)}
                            </Box>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Rationale */}
                {rationale && rationale.length > 0 && (
                    <Card 
                        bg={recommendationBg} 
                        borderRadius="lg" 
                        boxShadow="sm" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Heading size="sm" mb={3} color={headingTextColor}>
                                Recommendations
                            </Heading>
                            <VStack align="stretch" spacing={2}>
                                {rationale.map((item, index) => (
                                    <HStack key={index}>
                                        <Badge colorScheme="blue" fontSize="xs">
                                            {index + 1}
                                        </Badge>
                                        <Text fontSize="sm" color={labelTextColor}>
                                            {item}
                                        </Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>
                )}
                </VStack>
                </Box>

                {/* Metadata */}
                <Card 
                    bg={placeholderBg} 
                    borderRadius="lg" 
                    boxShadow="sm" 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            <Text fontSize="xs" color={mutedTextColor}>
                                Scored at: {new Date(latestScore.scored_at).toLocaleString()}
                            </Text>
                            <Text fontSize="xs" color={mutedTextColor}>
                                Snapshot ID: {latestScore.snapshot_id}
                            </Text>
                            {scoreData.count && (
                                <Text fontSize="xs" color={mutedTextColor}>
                                    Total Records: {scoreData.count}
                                </Text>
                            )}
                        </SimpleGrid>
                    </CardBody>
                </Card>
            </VStack>
        );
    };

    return (
        <Container maxW="container.xl" py={6} ref={containerRef}>
            <VStack spacing={6} align="stretch">
                {/* Header */}
                <Box>
                    <Heading size="lg" color={headingTextColor} mb={2}>
                        Compliance - Customer Bias Analysis
                    </Heading>
                    <Text color={bodyTextColor} fontSize="sm">
                        Compliance view: Monitor and review behavioral biases across all customers
                    </Text>
                </Box>

                {/* Search and Filters */}
                <Card 
                    bg={cardBg} 
                    borderRadius="lg" 
                    boxShadow="md" 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <CardBody>
                        <HStack spacing={4}>
                            <InputGroup flex={1}>
                                <InputLeftElement pointerEvents="none">
                                    <SearchIcon color={mutedTextColor} />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search customers..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    borderColor={borderColor}
                                    _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                    bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                    color={bodyTextColor}
                                />
                            </InputGroup>
                            <Button
                                bg={primaryColor}
                                color="white"
                                onClick={handleSearch}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Search
                            </Button>
                        </HStack>
                    </CardBody>
                </Card>

                {/* Customer List */}
                {loading ? (
                    <Flex justify="center" align="center" py={20}>
                        <Spinner size="xl" color={primaryColor} />
                    </Flex>
                ) : customers.length === 0 ? (
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <EmptyStateCard
                                icon={SearchIcon}
                                title={searchTerm ? 'No customers match this search' : 'Compliance scorecards will appear here once data is ready'}
                                description={
                                    searchTerm
                                        ? `We could not find any customers matching "${searchTerm}".`
                                        : 'Once customer data has been processed, this section will surface bias and compliance detail for each profile.'
                                }
                                hint={
                                    searchTerm
                                        ? 'Try a broader search or clear it to return to the full customer set.'
                                        : 'If a dataset was uploaded recently, let processing complete and then refresh this section.'
                                }
                                bg={placeholderBg}
                                borderColor={borderColor}
                                titleColor={headingTextColor}
                                descriptionColor={bodyTextColor}
                                hintColor={mutedTextColor}
                                iconColor={isDarkTheme ? accentColor : primaryColor}
                                maxW="full"
                            >
                                {searchTerm ? (
                                    <Button
                                        variant="outline"
                                        borderColor={primaryColor}
                                        color={primaryColor}
                                        _hover={{ bg: infoBg }}
                                        onClick={handleClearSearch}
                                    >
                                        Clear search
                                    </Button>
                                ) : (
                                    <Button
                                        bg={primaryColor}
                                        color="white"
                                        _hover={{ bg: buttonHoverBg }}
                                        onClick={() => fetchCustomers()}
                                    >
                                        Refresh customers
                                    </Button>
                                )}
                            </EmptyStateCard>
                        </CardBody>
                    </Card>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {customers.map((customer) => (
                            <Card
                                key={customer.customer_id || customer.id}
                                ref={(el) => customerRefs.current[customer.customer_id || customer.id] = el}
                                bg={cardBg}
                                borderRadius="lg"
                                boxShadow="md"
                                transition="all 0.2s"
                                _hover={{ boxShadow: 'lg' }}
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Flex justify="space-between" align="center">
                                        <VStack align="start" spacing={1} flex={1}>
                                            <Heading size="sm" color={headingTextColor}>
                                                {customer.customer_id || customer.name || 'Unknown Customer'}
                                            </Heading>
                                            <HStack spacing={4}>
                                                {customer.rm_name && (
                                                    <Text fontSize="sm" color={bodyTextColor}>
                                                        RM: {customer.rm_name}
                                                    </Text>
                                                )}
                                                {customer.email && (
                                                    <Text fontSize="sm" color={bodyTextColor}>
                                                        {customer.email}
                                                    </Text>
                                                )}
                                            </HStack>
                                        </VStack>
                                        <IconButton
                                            icon={
                                                expandedCustomer === (customer.customer_id || customer.id) ? (
                                                    <ChevronUpIcon />
                                                ) : (
                                                    <ChevronDownIcon />
                                                )
                                            }
                                            onClick={() => toggleCustomer(customer.customer_id || customer.id)}
                                            colorScheme="blue"
                                            variant="ghost"
                                            aria-label="Toggle details"
                                        />
                                    </Flex>

                                    <Collapse in={expandedCustomer === (customer.customer_id || customer.id)} animateOpacity>
                                        <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                                            {renderCustomerScores(customer.customer_id || customer.id)}
                                        </Box>
                                    </Collapse>
                                </CardBody>
                            </Card>
                        ))}
                    </VStack>
                )}

                {/* Pagination */}
                {!loading && customers.length > 0 && (
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Flex justify="space-between" align="center">
                                <Text fontSize="sm" color={bodyTextColor}>
                                    Showing {customers.length} of {pagination.total_count} customers
                                </Text>
                                <HStack>
                                    <Button
                                        size="sm"
                                        onClick={() => fetchCustomers(pagination.page - 1, searchTerm)}
                                        isDisabled={pagination.page === 1}
                                        variant="outline"
                                        borderColor={borderColor}
                                        color={isDarkTheme ? accentColor : primaryColor}
                                        _hover={{ bg: infoBg }}
                                    >
                                        Previous
                                    </Button>
                                    <Text fontSize="sm" color={bodyTextColor}>
                                        Page {pagination.page}
                                    </Text>
                                    <Button
                                        size="sm"
                                        onClick={() => fetchCustomers(pagination.page + 1, searchTerm)}
                                        isDisabled={customers.length < pagination.page_size}
                                        variant="outline"
                                        borderColor={borderColor}
                                        color={isDarkTheme ? accentColor : primaryColor}
                                        _hover={{ bg: infoBg }}
                                    >
                                        Next
                                    </Button>
                                </HStack>
                            </Flex>
                        </CardBody>
                    </Card>
                )}
            </VStack>
        </Container>
    );
}

export default DashboardComplianceScoresPaid;
